/**
 * API Configuration
 * Dynamically determines the API endpoints based on the current host
 */

interface ApiConfig {
  wsUrl: string;
  apiUrl: string;
  host: string;
  apiPort?: number;
  wsPort?: number;
  isProduction: boolean;
}

const DEFAULT_WS_PORT = 8765;

function isLocalHostname(hostname: string): boolean {
  return (
    ['localhost', '127.0.0.1'].includes(hostname) ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.startsWith('172.')
  );
}

function resolvePort(url: URL): number | undefined {
  if (url.port) {
    return parseInt(url.port, 10);
  }

  if (url.protocol === 'https:' || url.protocol === 'wss:') {
    return 443;
  }
  if (url.protocol === 'http:' || url.protocol === 'ws:') {
    return 80;
  }

  return undefined;
}

/**
 * Get the API configuration based on the current environment
 */
export function getApiConfig(): ApiConfig {
  // Check if we have environment variables set
  const envWsUrl = import.meta.env.VITE_WS_URL;
  const envApiUrl = import.meta.env.VITE_API_URL;
  
  // If environment variables are set, use them (allowing either URL to be provided)
  if (envWsUrl || envApiUrl) {
    const fallbackProtocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
    const fallbackHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const wsProtocol = fallbackProtocol === 'https:' ? 'wss:' : 'ws:';
    
    const apiUrlObj = new URL(
      envApiUrl ?? `${fallbackProtocol}//${fallbackHost}/api`,
    );
    const wsUrlObj = new URL(
      envWsUrl ?? `${wsProtocol}//${apiUrlObj.hostname}:${DEFAULT_WS_PORT}/ws`,
    );

    return {
      wsUrl: wsUrlObj.toString(),
      apiUrl: apiUrlObj.toString(),
      host: apiUrlObj.hostname,
      apiPort: resolvePort(apiUrlObj),
      wsPort: resolvePort(wsUrlObj) ?? DEFAULT_WS_PORT,
      isProduction: !isLocalHostname(apiUrlObj.hostname),
    };
  }
  
  // Otherwise, dynamically determine based on current location
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  
  // Determine if we're in production (not localhost or local IP)
  const isProduction = !isLocalHostname(currentHost);
  const wsProtocol = currentProtocol === 'https:' ? 'wss:' : 'ws:';

  const wsPort = DEFAULT_WS_PORT;

  // Build the URLs based on the current host
  let wsUrl: string;
  let apiUrl: string;
  
  let apiPort: number | undefined;

  if (isProduction) {
    const apiUrlObj = new URL(`${currentProtocol}//${currentHost}/api`);
    apiUrl = apiUrlObj.toString();
    apiPort = resolvePort(apiUrlObj);
    wsUrl = `${wsProtocol}//${currentHost}/ws`;
  } else {
    // In development, explicitly use localhost to ensure WebSocket works
    // Some browsers have issues with dynamic host resolution for WebSockets
    const devHost = ['localhost', '127.0.0.1'].includes(currentHost) 
      ? 'localhost'  // Force localhost for consistency
      : currentHost; // Use actual host for other local IPs
      
    wsUrl = `${wsProtocol}//${devHost}:${wsPort}/ws`;
    apiUrl = '/api';
  }
  
  return {
    wsUrl,
    apiUrl,
    host: currentHost,
    apiPort,
    wsPort,
    isProduction,
  };
}

/**
 * Get a singleton instance of the API config
 */
let cachedConfig: ApiConfig | null = null;

export function getApiConfigCached(): ApiConfig {
  if (!cachedConfig) {
    cachedConfig = getApiConfig();
    
    // Log the configuration for debugging
    console.log('[API Config] Initialized:', {
      wsUrl: cachedConfig.wsUrl,
      apiUrl: cachedConfig.apiUrl,
      host: cachedConfig.host,
      apiPort: cachedConfig.apiPort,
      wsPort: cachedConfig.wsPort,
      isProduction: cachedConfig.isProduction,
    });
  }
  
  return cachedConfig;
}

/**
 * Reset the cached configuration (useful for testing)
 */
export function resetApiConfig(): void {
  cachedConfig = null;
}

// Clear any stale cached config on module load
if (typeof window !== 'undefined') {
  // Force fresh config on page reload
  cachedConfig = null;
}

// Export the default configuration
export default getApiConfigCached();
